import { useMemo, useState } from "react";
import axios from "axios";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarClock, CheckCircle2, Undo2, Loader2, AlertTriangle, Clock3, ChevronRight, CalendarRange, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const FOLLOW_UP_STATUSES = ["Contacted", "Demoed", "Subscribed"];
const FOLLOW_UP_DAYS = [3, 7, 14];

/**
 * Wider, denser follow-ups card optimised for the main (left) column.
 * - Each stage shown as a horizontal block (Contacted / Demoed / Subscribed)
 * - Inside each stage, 3 tiles side-by-side (+3d / +7d / +14d)
 * - Current stage tiles are interactive; previous stages are read-only & muted
 * - Stage badge + progress (e.g. "1 of 3 completed") in header
 */
export default function FollowUpsCard({ lead, onChange, statusColors }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [dialog, setDialog] = useState({ open: false, fu: null });
  const [mode, setMode] = useState("done"); // "done" | "reschedule"
  const [note, setNote] = useState("");
  const [newDate, setNewDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [waMessage, setWaMessage] = useState("");

  // Group follow-ups by status
  const grouped = useMemo(() => {
    const map = {};
    (lead?.follow_ups || []).forEach((fu) => {
      if (!map[fu.status]) map[fu.status] = {};
      map[fu.status][fu.day] = fu;
    });
    return FOLLOW_UP_STATUSES
      .filter((s) => map[s])
      .map((s) => ({ status: s, items: FOLLOW_UP_DAYS.map((d) => map[s][d]).filter(Boolean) }));
  }, [lead?.follow_ups]);

  // Calculate min/max allowed dates for reschedule based on next follow-up in same status.
  // Must be called BEFORE any conditional early return to respect rules-of-hooks.
  const rescheduleBounds = useMemo(() => {
    if (!dialog.fu) return { min: "", max: "" };
    const fu = dialog.fu;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const min = format(tomorrow, "yyyy-MM-dd");
    // Find next follow-up in the same stage with a higher day value
    const sameStage = (lead?.follow_ups || []).filter((x) => x.status === fu.status && x.day > fu.day).sort((a, b) => a.day - b.day);
    if (sameStage.length > 0) {
      try {
        const nextDue = parseISO(sameStage[0].due_date);
        const oneDayBefore = new Date(nextDue);
        oneDayBefore.setDate(oneDayBefore.getDate() - 1);
        return { min, max: format(oneDayBefore, "yyyy-MM-dd"), nextDue: sameStage[0].due_date };
      } catch {
        return { min, max: "" };
      }
    }
    return { min, max: "" };
  }, [dialog.fu, lead?.follow_ups]);

  if (grouped.length === 0) {
    return (
      <Card className="border border-dashed border-gray-200 rounded-xl bg-gray-50" data-testid="follow-ups-card">
        <CardContent className="p-6 text-center">
          <CalendarClock className="w-7 h-7 text-gray-300 mx-auto mb-2" />
          <h2 className="text-sm font-semibold text-gray-700 mb-1">No follow-ups yet</h2>
          <p className="text-xs text-gray-500">Once the lead reaches <strong>Contacted</strong>, three follow-ups (+3 / +7 / +14 days) will be created automatically.</p>
        </CardContent>
      </Card>
    );
  }

  const openRecord = (fu) => {
    setDialog({ open: true, fu });
    setMode("done");
    setNote(fu.note || "");
    setNewDate("");
    setWaMessage("");
  };

  const recordFollowUp = async () => {
    if (!dialog.fu) return;
    if (mode === "reschedule" && !newDate) { toast.error("Pick a new date"); return; }
    setSaving(true);
    try {
      const payload = {
        status: dialog.fu.status,
        day: dialog.fu.day,
        note: note.trim() || null,
        action: mode,
      };
      if (mode === "reschedule") payload.new_due_date = newDate;
      if (mode === "reschedule" && waMessage.trim()) payload.wa_message = waMessage.trim();
      await axios.post(`${API}/leads/${lead.id}/follow-ups`, payload);
      toast.success(mode === "reschedule" ? `Follow-up rescheduled to ${format(parseISO(newDate), "d MMM")}` : `${dialog.fu.day}-day follow-up recorded`);
      setDialog({ open: false, fu: null });
      setNote("");
      setNewDate("");
      setWaMessage("");
      setMode("done");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save follow-up");
    } finally { setSaving(false); }
  };

  const undoFollowUp = async (fu) => {
    if (!window.confirm(`Undo the ${fu.day}-day follow-up?`)) return;
    try {
      await axios.delete(`${API}/leads/${lead.id}/follow-ups`, { params: { status: fu.status, day: fu.day } });
      toast.success("Follow-up cleared");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to undo");
    }
  };

  return (
    <>
      <Card className="border border-gray-200 rounded-xl bg-white" data-testid="follow-ups-card">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-[#0D0D5B]" /> Follow-ups
              </h2>
              <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium px-1.5 py-0.5 bg-gray-100 rounded">3 / 7 / 14 days</span>
            </div>
            <p className="text-[11px] text-gray-400 italic">Auto-created on status change</p>
          </div>

          <div className="space-y-4">
            {grouped.map(({ status, items }) => {
              const isActive = status === lead.status;
              const done = items.filter((f) => f.done_at).length;
              return (
                <StageBlock
                  key={status}
                  status={status}
                  items={items}
                  isActive={isActive}
                  doneCount={done}
                  totalCount={items.length}
                  statusColors={statusColors}
                  onRecord={openRecord}
                  onUndo={undoFollowUp}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, fu: null })}>
        <DialogContent className="sm:max-w-[480px]" data-testid="record-followup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0D0D5B]">
              <CalendarClock className="w-5 h-5" />
              {dialog.fu?.done_at ? "Edit follow-up note" : mode === "reschedule" ? `Reschedule ${dialog.fu?.day}-day follow-up` : `Record ${dialog.fu?.day}-day follow-up`}
            </DialogTitle>
            <DialogDescription>
              {dialog.fu && (
                <>Stage: <strong>{dialog.fu.status}</strong> · Due: <strong>{dialog.fu.due_date ? format(parseISO(dialog.fu.due_date), "d MMM yyyy") : "—"}</strong>{rescheduleBounds.nextDue && (<> · Next FU due <strong>{format(parseISO(rescheduleBounds.nextDue), "d MMM yyyy")}</strong></>)}</>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Mode tabs — only when not editing an already-done entry */}
          {!dialog.fu?.done_at && (
            <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg" data-testid="fu-mode-tabs">
              <button
                type="button"
                onClick={() => setMode("done")}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "done" ? "bg-white text-[#0D0D5B] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                data-testid="fu-mode-done"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark Complete
              </button>
              <button
                type="button"
                onClick={() => setMode("reschedule")}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "reschedule" ? "bg-white text-amber-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                data-testid="fu-mode-reschedule"
                disabled={!rescheduleBounds.max && !!(lead?.follow_ups || []).filter((x) => x.status === dialog.fu?.status && x.day > dialog.fu?.day).length}
              >
                <CalendarRange className="w-3.5 h-3.5" /> Reschedule
              </button>
            </div>
          )}

          <div className="py-2 space-y-3">
            {mode === "reschedule" && !dialog.fu?.done_at && (
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">New Due Date</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={rescheduleBounds.min}
                  max={rescheduleBounds.max || undefined}
                  className="w-full border border-gray-300 rounded-lg text-sm px-3 py-2 focus:ring-1 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  data-testid="followup-new-date"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Must be after today{rescheduleBounds.max ? ` and before ${format(parseISO(rescheduleBounds.max), "d MMM yyyy")} (a day before the next follow-up)` : ""}.
                </p>
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">{mode === "reschedule" ? "Reason / Note" : "Remark / Note"}</label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder={mode === "reschedule" ? "Why are we rescheduling? (e.g. customer asked to call next week)" : "What did you discuss? Any next steps?"} className="border-gray-300 rounded-lg text-sm resize-none" data-testid="followup-note-textarea" />
            </div>
            {mode === "reschedule" && !dialog.fu?.done_at && (
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block flex items-center gap-1">
                  Custom WhatsApp message <span className="text-gray-400 font-normal">(optional override)</span>
                </label>
                <Textarea
                  value={waMessage}
                  onChange={(e) => setWaMessage(e.target.value)}
                  rows={4}
                  placeholder="Leave blank to use the saved Reschedule template. Type a one-off message to override for this lead only."
                  className="border-gray-300 rounded-lg text-sm resize-none"
                  data-testid="followup-wa-message-textarea"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Sent to <strong>{lead?.name}</strong> via WhatsApp{lead?.wa_optout ? <span className="text-red-500"> · Opt-out is active — no message will be sent</span> : null}.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog({ open: false, fu: null })} data-testid="followup-cancel-btn">Cancel</Button>
            {mode === "reschedule" && !dialog.fu?.done_at ? (
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={saving || !newDate} onClick={recordFollowUp} data-testid="followup-confirm-reschedule-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarRange className="w-4 h-4 mr-2" />}
                Save Reschedule
              </Button>
            ) : (
              <Button className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white" disabled={saving} onClick={recordFollowUp} data-testid="followup-confirm-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {dialog.fu?.done_at ? "Update Note" : "Mark Complete"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StageBlock({ status, items, isActive, doneCount, totalCount, statusColors, onRecord, onUndo }) {
  const headerBg = isActive ? "bg-gradient-to-r from-[#0D0D5B]/5 to-transparent" : "bg-gray-50";
  return (
    <div className={`rounded-xl border ${isActive ? "border-[#0D0D5B]/20 bg-white shadow-sm" : "border-gray-200 bg-gray-50"} overflow-hidden`} data-testid={`fu-group-${status.toLowerCase()}`}>
      <div className={`flex items-center justify-between px-4 py-2.5 ${headerBg} border-b ${isActive ? "border-[#0D0D5B]/10" : "border-gray-200"}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${statusColors?.[status] || "bg-gray-100 text-gray-700"} ${!isActive ? "opacity-60" : ""}`}>
            {status}
          </span>
          {isActive ? (
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Current stage</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Previous stage · read-only</span>
          )}
        </div>
        <span className={`text-[11px] font-semibold tabular-nums ${doneCount === totalCount ? "text-emerald-600" : isActive ? "text-[#0D0D5B]" : "text-gray-500"}`}>
          {doneCount} / {totalCount} completed
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4">
        {items.map((fu) => <FollowUpTile key={`${fu.status}-${fu.day}`} fu={fu} isActive={isActive} onRecord={onRecord} onUndo={onUndo} />)}
      </div>
    </div>
  );
}

function FollowUpTile({ fu, isActive, onRecord, onUndo }) {
  const isDone = !!fu.done_at;
  const hasRescheduled = Array.isArray(fu.reschedule_history) && fu.reschedule_history.length > 0;
  const today = new Date();
  let dueDate;
  try { dueDate = parseISO(fu.due_date); } catch { dueDate = null; }
  const daysUntilDue = dueDate ? differenceInCalendarDays(dueDate, today) : null;
  const isOverdue = !isDone && daysUntilDue !== null && daysUntilDue < 0;
  const isDueToday = !isDone && daysUntilDue === 0;

  const dueLabel = (() => {
    if (!dueDate) return "—";
    if (isDone) return format(dueDate, "d MMM");
    if (isDueToday) return "Due today";
    if (isOverdue) return `Overdue ${Math.abs(daysUntilDue)}d`;
    if (daysUntilDue === 1) return "Tomorrow";
    if (daysUntilDue > 0) return `In ${daysUntilDue}d`;
    return format(dueDate, "d MMM");
  })();

  // Visual tone of the tile based on state
  const tone = isDone
    ? "bg-emerald-50 border-emerald-200"
    : !isActive
    ? "bg-white border-gray-200 opacity-60"
    : isOverdue
    ? "bg-red-50 border-red-200"
    : isDueToday
    ? "bg-amber-50 border-amber-300 ring-1 ring-amber-200"
    : hasRescheduled
    ? "bg-amber-50/40 border-amber-200"
    : "bg-white border-gray-200";

  const dayBadgeTone = isDone
    ? "bg-emerald-600 text-white"
    : isOverdue && isActive
    ? "bg-red-600 text-white"
    : isDueToday && isActive
    ? "bg-amber-600 text-white"
    : "bg-[#0D0D5B] text-white";

  const lastReschedule = hasRescheduled ? fu.reschedule_history[fu.reschedule_history.length - 1] : null;

  return (
    <div className={`relative rounded-lg border ${tone} p-3 flex flex-col`} data-testid={`fu-row-${fu.status.toLowerCase()}-${fu.day}`}>
      {/* Header row: day badge + due label */}
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center justify-center min-w-[36px] h-5 rounded-md text-[11px] font-bold tabular-nums ${dayBadgeTone}`}>+{fu.day}d</span>
        <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider">
          {isDone ? (
            <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Done</span>
          ) : !isActive ? (
            <span className="text-gray-400">Skipped</span>
          ) : hasRescheduled ? (
            <span className="text-amber-700 inline-flex items-center gap-1" data-testid="fu-reschedule-badge"><CalendarRange className="w-3 h-3" /> Rescheduled · {dueLabel}</span>
          ) : isOverdue ? (
            <span className="text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {dueLabel}</span>
          ) : isDueToday ? (
            <span className="text-amber-700 inline-flex items-center gap-1"><Clock3 className="w-3 h-3" /> {dueLabel}</span>
          ) : (
            <span className="text-gray-500">{dueLabel}</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="text-xs text-gray-700 flex-1 min-h-[36px]">
        {isDone ? (
          <>
            {fu.note ? (
              <p className="text-gray-700 leading-snug whitespace-pre-wrap break-words line-clamp-3" title={fu.note}>{fu.note}</p>
            ) : (
              <p className="text-gray-400 italic">No note recorded</p>
            )}
          </>
        ) : hasRescheduled && lastReschedule?.note ? (
          <p className="text-amber-800 leading-snug whitespace-pre-wrap break-words line-clamp-3 italic" title={lastReschedule.note}>"{lastReschedule.note}"</p>
        ) : (
          <p className="text-gray-400 text-[11px] leading-snug">{isActive ? "Awaiting follow-up. Click Record once you've reached out." : "—"}</p>
        )}
      </div>

      {/* Footer row: meta + actions */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between gap-1">
        <div className="text-[10px] text-gray-500 truncate flex-1">
          {isDone && fu.done_by ? (
            (() => {
              let stamp = "";
              try { stamp = ` · ${format(parseISO(fu.done_at), "d MMM yyyy, HH:mm")}`; } catch {}
              return (
                <span title={fu.done_at ? format(parseISO(fu.done_at), "EEEE, d MMM yyyy 'at' HH:mm:ss") : ""}>
                  by <span className="font-semibold text-emerald-700">{fu.done_by}</span>{stamp}
                </span>
              );
            })()
          ) : hasRescheduled ? (
            (() => {
              let stamp = "";
              try { stamp = format(parseISO(lastReschedule.rescheduled_at), "d MMM, HH:mm"); } catch {}
              return (
                <span className="inline-flex items-center gap-0.5 text-amber-700" title={`Rescheduled by ${lastReschedule.by} at ${stamp}`}>
                  <RotateCcw className="w-2.5 h-2.5" /> {fu.reschedule_history.length}×
                  <span className="text-gray-500"> · by <span className="font-semibold text-amber-700">{lastReschedule.by}</span></span>
                </span>
              );
            })()
          ) : !isDone ? (
            <span>Due {dueDate ? format(dueDate, "d MMM") : "—"}</span>
          ) : null}
        </div>

        {isActive && (
          isDone ? (
            <div className="flex items-center gap-0.5 shrink-0">
              <button onClick={() => onRecord(fu)} className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900 px-1.5 py-1 rounded hover:bg-white" data-testid={`fu-edit-${fu.day}`}>
                Edit
              </button>
              <button onClick={() => onUndo(fu)} className="text-emerald-700 hover:text-red-600 p-1 rounded hover:bg-white" title="Undo" data-testid={`fu-undo-${fu.day}`}>
                <Undo2 className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button onClick={() => onRecord(fu)} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-[#0D0D5B] hover:bg-[#1a1a7a] px-2 py-1 rounded-md shrink-0" data-testid={`fu-record-${fu.day}`}>
              Record <ChevronRight className="w-3 h-3" />
            </button>
          )
        )}
      </div>
    </div>
  );
}
